import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { FolderResponseDto } from '@inference.engine/workspace-api-client/web';

import { notificationService } from '@/lib/services/notification.service';
import { parseProblemDetail } from '@/shared/utils/problem-details';

import {
  setProjectFolderApprovalChain,
  type FolderApprovalChainInput,
} from './project-folders.api';

export { setProjectFolderApprovalChain };
export type { FolderApprovalChainInput };

interface UseSetFolderApprovalChainOptions {
  projectId: string;
  folderId: string;
  onSuccess?: () => void;
}

export function useSetFolderApprovalChain(options: UseSetFolderApprovalChainOptions) {
  const { projectId, folderId, onSuccess } = options;
  const { t } = useTranslation('projects');
  const queryClient = useQueryClient();

  return useMutation<FolderResponseDto, Error, FolderApprovalChainInput>({
    mutationFn: (input) => setProjectFolderApprovalChain(projectId, folderId, input),
    onSuccess: (_folder, input) => {
      notificationService.showSuccess(
        input.approvalChainId === null
          ? t('manageProjectFolders.useSetFolderApprovalChain.approvalChainCleared')
          : t('manageProjectFolders.useSetFolderApprovalChain.approvalChainSet'),
      );
      void queryClient.invalidateQueries({
        queryKey: ['project', projectId, 'folders'],
      });
      onSuccess?.();
    },
    onError: (error: unknown) => {
      notificationService.showError(
        parseProblemDetail(
          error,
          t('manageProjectFolders.useSetFolderApprovalChain.couldNotSetApprovalChain'),
        ).detail,
      );
    },
  });
}
